import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Building, Check, ChevronRight, Mail, Lock, User, Phone, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterWithPlan() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nombre: '',
    empresa: '',
    telefono: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans`);
      setPlans(response.data);
      // Select basic by default
      const basicPlan = response.data.find(p => p.name === 'Básico');
      if (basicPlan) setSelectedPlan(basicPlan);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error al cargar los planes');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNextStep = () => {
    if (step === 1 && !selectedPlan) {
      toast.error('Selecciona un plan para continuar');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const planKey = selectedPlan.name === 'Básico' ? 'basic' 
        : selectedPlan.name === 'Profesional' ? 'professional' 
        : 'enterprise';

      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        email: formData.email,
        password: formData.password,
        nombre: formData.nombre,
        empresa: formData.empresa || null,
        telefono: formData.telefono || null,
        plan: planKey,
        max_propiedades: selectedPlan.max_properties
      });

      const { token, user } = response.data;
      login(token, user);
      toast.success('Cuenta creada exitosamente');
      navigate('/onboarding');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-blue-600 rounded-lg flex items-center justify-center">
              <Building className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white" style={{fontFamily: 'Manrope'}}>
              Edén
            </span>
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2" style={{fontFamily: 'Manrope'}}>
            Crea tu cuenta
          </h1>
          <p className="text-slate-400">
            {step === 1 ? 'Paso 1: Selecciona tu plan' : 'Paso 2: Completa tus datos'}
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-slate-600'}`}></div>
            <div className={`w-12 h-1 ${step >= 2 ? 'bg-primary' : 'bg-slate-600'}`}></div>
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-slate-600'}`}></div>
          </div>
        </div>

        {step === 1 ? (
          /* Step 1: Plan Selection */
          <div className="space-y-8">
            <div className="grid md:grid-cols-3 gap-6">
              {plans.map((plan) => {
                const isSelected = selectedPlan?.name === plan.name;
                const isPro = plan.name === 'Profesional';
                
                return (
                  <Card 
                    key={plan.name}
                    className={`cursor-pointer transition-all duration-300 ${
                      isSelected 
                        ? 'border-primary border-2 shadow-lg shadow-primary/20' 
                        : 'border-slate-700 hover:border-slate-500'
                    } ${isPro ? 'relative' : ''} bg-slate-800/50`}
                    onClick={() => setSelectedPlan(plan)}
                    data-testid={`plan-${plan.name.toLowerCase()}`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          POPULAR
                        </span>
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="text-white" style={{fontFamily: 'Manrope'}}>
                        {plan.name}
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        Hasta {plan.max_properties === 9999 ? 'ilimitadas' : plan.max_properties} propiedades
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="text-center">
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                        <span className="text-slate-400">/mes</span>
                      </div>
                      
                      <ul className="space-y-3 text-left">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                            <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      {isSelected && (
                        <div className="mt-6 flex items-center justify-center gap-2 text-primary font-medium">
                          <Check className="h-5 w-5" />
                          Seleccionado
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <div className="flex justify-center">
              <Button 
                onClick={handleNextStep}
                size="lg"
                className="bg-primary hover:bg-primary/90 px-8"
                data-testid="continue-to-form-btn"
              >
                Continuar
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-center text-slate-400 text-sm">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Inicia sesión
              </Link>
            </p>
          </div>
        ) : (
          /* Step 2: Registration Form */
          <div className="max-w-md mx-auto">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-white" style={{fontFamily: 'Manrope'}}>
                  Completa tu registro
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Plan seleccionado: <span className="text-primary font-medium">{selectedPlan?.name}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre" className="text-slate-200">Nombre completo *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="nombre"
                        placeholder="Tu nombre"
                        value={formData.nombre}
                        onChange={(e) => handleChange('nombre', e.target.value)}
                        required
                        className="pl-10 bg-slate-900 border-slate-600 text-white"
                        data-testid="register-nombre"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-200">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        className="pl-10 bg-slate-900 border-slate-600 text-white"
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password" className="text-slate-200">Contraseña *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="password"
                          type="password"
                          placeholder="******"
                          value={formData.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          required
                          className="pl-10 bg-slate-900 border-slate-600 text-white"
                          data-testid="register-password"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-slate-200">Confirmar *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type="password"
                          placeholder="******"
                          value={formData.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          required
                          className="pl-10 bg-slate-900 border-slate-600 text-white"
                          data-testid="register-confirm-password"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="empresa" className="text-slate-200">Empresa (Opcional)</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="empresa"
                        placeholder="Nombre de tu empresa"
                        value={formData.empresa}
                        onChange={(e) => handleChange('empresa', e.target.value)}
                        className="pl-10 bg-slate-900 border-slate-600 text-white"
                        data-testid="register-empresa"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telefono" className="text-slate-200">Teléfono (Opcional)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        id="telefono"
                        placeholder="+57 300 123 4567"
                        value={formData.telefono}
                        onChange={(e) => handleChange('telefono', e.target.value)}
                        className="pl-10 bg-slate-900 border-slate-600 text-white"
                        data-testid="register-telefono"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline"
                      className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                      onClick={() => setStep(1)}
                    >
                      Volver
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-primary hover:bg-primary/90"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creando...' : 'Crear Cuenta'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
